import { useState, MouseEvent } from 'react'
import { LogoutOptions } from '@auth0/auth0-spa-js'
import {
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
} from '@black-bear-energy/rucksack'
import { SupervisorAccountRounded, Logout } from '@mui/icons-material'
import { IAuthUser } from '../../state/modules/auth'
import { isProductionHost } from '../../utils/enviroment'
import { NAV_VISIBILITY_KEY } from './constants'

/** Props for {@link UserAvatarMenu}. */
interface UserAvatarMenuProps {
  /** Auth0 logout handler. */
  logout: (options?: LogoutOptions) => void
  /** Whether the current user has permission to impersonate other users. */
  canImpersonate: boolean
  /** Callback invoked when the user selects the impersonation menu item. */
  onImpersonate: () => void
  /** Currently authenticated user, used for the avatar and display name. */
  user?: IAuthUser
}

/** Avatar button with dropdown menu showing user info and actions. */
export function UserAvatarMenu({
  logout,
  canImpersonate,
  onImpersonate,
  user,
}: UserAvatarMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    const returnTo = isProductionHost()
      ? process.env.REACT_APP_AUTH0_RETURNTO
      : process.env.REACT_APP_AUTH0_STAGING_RETURNTO
    try {
      localStorage.removeItem(NAV_VISIBILITY_KEY)
    } catch (error) {
      console.warn('Failed to remove nav-visibility from localStorage', error)
    }
    handleClose()
    logout({ returnTo })
  }

  const handleImpersonate = () => {
    handleClose()
    onImpersonate()
  }

  const hasGivenOrFamilyName = !!user?.given_name || !!user?.family_name
  const primaryText = hasGivenOrFamilyName
    ? [user.given_name, user.family_name].filter(Boolean).join(' ')
    : user?.email
  const secondaryText = hasGivenOrFamilyName ? user?.email : null

  const avatar = <Avatar src={user?.picture} />

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ padding: 0.5 }}>
        {avatar}
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            paddingX: 2,
            paddingY: 1,
            pointerEvents: 'none',
          }}
        >
          {avatar}
          <ListItemText
            primary={primaryText}
            secondary={secondaryText}
            sx={{ margin: 0 }}
          />
        </Box>
        <Divider sx={{ paddingTop: 1 }} />
        <Box sx={{ paddingTop: 2 }}>
          {canImpersonate && (
            <MenuItem onClick={handleImpersonate}>
              <ListItemIcon>
                <SupervisorAccountRounded />
              </ListItemIcon>
              Impersonate a user
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Box>
      </Menu>
    </>
  )
}
